import BaseCss from 'style/base'
import TypographyCss from 'style/typography'
import { useLocation } from 'react-router-dom'
import { useEffect, useLayoutEffect } from 'react'

/**
 * Federated remotes ship their own GlobalStyle (10px rem base). Skip host
 * TypographyCss on those routes so marketing body type (weight 300, letter-
 * spacing, a:hover opacity) does not override the remote. BaseCss stays —
 * its anchor reset is intentional host-wide.
 */
const REMOTE_SHELL_PREFIXES = ['/dashboard', '/admin', '/invest', '/onboarding'] as const

const isRemoteShellPath = (pathname: string) =>
  REMOTE_SHELL_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

/** useLayoutEffect on the client (class must land before paint), no-op on the server. */
const useMarkerEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export const HostGlobalStyles = () => {
  const { pathname } = useLocation()
  const remoteShell = isRemoteShellPath(pathname)

  /*
   * Not mounting TypographyCss (below) is not enough on its own: under SSR the
   * server-rendered copy of that sheet is rehydrated into a group the client's
   * createGlobalStyle never claims, so unmounting removes only the client's copy
   * and orphans the server's permanently — global `h3 { …!important }` then
   * outlives the host route and flattens the remote's headings. This class lets
   * the selectors in typography.js opt out instead of relying on removal.
   */
  useMarkerEffect(() => {
    document.body.classList.toggle('mf-remote-route', remoteShell)
    return () => document.body.classList.remove('mf-remote-route')
  }, [remoteShell])

  return (
    <>
      {/* BaseCss owns the host's Ionicons (hashed @font-face + glyph CSS) so icons are
          in the SSR HTML. Library components add their own copy on the client via
          IoniconsStyles (>= 3.1.0 keeps it mounted), so don't add another here. */}
      <BaseCss />
      {!remoteShell && <TypographyCss />}
    </>
  )
}
